import { useEffect, useState } from "react";
import { Shield, AlertTriangle, Clock, RefreshCw, TrendingUp, Activity, CheckCircle } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { groundOpsApi } from "@/api";

const RISK_STYLE = {
  low:      { badge: "bg-green-500/10 border-green-500/25 text-green-400",   bar: "#22c55e" },
  moderate: { badge: "bg-amber-500/10 border-amber-500/25 text-amber-400",   bar: "#f59e0b" },
  high:     { badge: "bg-orange-500/10 border-orange-500/25 text-orange-400", bar: "#f97316" },
  critical: { badge: "bg-red-500/10 border-red-500/30 text-red-400",         bar: "#ef4444" },
};

function ScoreGauge({ score }) {
  const pct = score;
  const color = pct < 35 ? "#22c55e" : pct < 60 ? "#f59e0b" : pct < 80 ? "#f97316" : "#ef4444";
  return (
    <div className="relative w-16 h-16 flex-shrink-0">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1e293b" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11px] font-black" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color = "text-white", icon: Icon }) {
  return (
    <div className="bg-[#0c1929] rounded-xl border border-slate-700/30 px-4 py-3 flex items-start gap-3">
      {Icon && <Icon size={18} className={color + " mt-0.5 flex-shrink-0"} />}
      <div>
        <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-1">{label}</div>
        <div className={`text-2xl font-black ${color}`}>{value}</div>
        {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a1828] border border-slate-600/30 rounded-lg px-3 py-2 text-xs text-white">
      <div className="font-bold">{payload[0]?.payload?.name}</div>
      <div className="text-slate-400">Avg score: {payload[0]?.value}</div>
    </div>
  );
};

export default function GroundFatigueShield() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [station, setStation] = useState("all");
  const [riskFilter, setRisk] = useState("all");
  const [expanded, setExpanded] = useState(null);

  function load() {
    setLoading(true);
    groundOpsApi.getFatigue(station !== "all" ? station : undefined)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, [station]);

  const allStaff    = data?.staff || [];
  const summary     = data?.summary || {};
  const stationMap  = data?.station_summary || {};
  const thresholds  = data?.thresholds || {};
  const stations    = Object.keys(stationMap).sort();

  const filtered = allStaff.filter(s =>
    riskFilter === "all" || s.risk_level === riskFilter
  );

  // Chart data: station avg scores
  const stationChartData = stations.map(st => ({
    name: st,
    score: stationMap[st]?.avg_score || 0,
  }));

  return (
    <div className="p-5 space-y-5 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <Shield size={20} className="text-orange-400" />
            <h1 className="text-xl font-black text-white">Ground Fatigue Shield</h1>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 pl-7">
            Duty-hour compliance and fatigue risk assessment for ground staff
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-orange-400 border border-slate-700/40 rounded-lg px-3 py-2 transition"
          data-testid="refresh-fatigue"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Regulatory thresholds banner */}
      <div className="flex flex-wrap gap-3 bg-[#0a1828] border border-orange-500/15 rounded-xl px-4 py-3">
        <span className="text-[9px] text-orange-400 font-bold uppercase tracking-widest mr-2">Limits:</span>
        {[
          { label: "Max 24h Duty",  value: `${thresholds.max_hours_24h || 12}h` },
          { label: "Max 7-day",     value: `${thresholds.max_hours_week || 60}h` },
          { label: "Min Rest",      value: `${thresholds.min_rest_hours || 8}h` },
          { label: "Max Consec Days", value: `${thresholds.max_consec_days || 6}d` },
        ].map(t => (
          <span key={t.label} className="text-[10px] text-slate-400">
            <span className="text-slate-600">{t.label}: </span>
            <span className="font-bold text-white">{t.value}</span>
          </span>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select
          value={station}
          onChange={e => setStation(e.target.value)}
          className="bg-[#0c1929] border border-slate-600/30 rounded-lg px-3 py-2 text-white text-[11px] focus:outline-none focus:border-orange-500/40"
          data-testid="fatigue-station-filter"
        >
          <option value="all">All Stations</option>
          {stations.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={riskFilter}
          onChange={e => setRisk(e.target.value)}
          className="bg-[#0c1929] border border-slate-600/30 rounded-lg px-3 py-2 text-white text-[11px] focus:outline-none focus:border-orange-500/40"
          data-testid="risk-filter"
        >
          <option value="all">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="moderate">Moderate</option>
          <option value="low">Low</option>
        </select>
      </div>

      {loading && <div className="text-center py-12 text-slate-600 animate-pulse text-sm">Assessing fatigue levels...</div>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard label="Avg Fatigue Score" value={summary.avg_fatigue_score} sub="Fleet-wide" icon={Activity} color="text-white" />
            <StatCard label="High / Critical" value={`${summary.high_risk + summary.critical_risk}`} sub="Need intervention" icon={AlertTriangle} color="text-red-400" />
            <StatCard label="Fit for Duty" value={summary.fit_for_duty} sub="No violations" icon={CheckCircle} color="text-green-400" />
          </div>

          {/* Station avg score chart */}
          {stationChartData.length > 0 && (
            <div className="bg-[#0c1929] rounded-xl border border-slate-700/30 p-4">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
                <TrendingUp size={11} /> Station Fatigue Scores
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={stationChartData} barCategoryGap="30%">
                  <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                    {stationChartData.map((entry, idx) => {
                      const color = entry.score < 35 ? "#22c55e" : entry.score < 60 ? "#f59e0b" : entry.score < 80 ? "#f97316" : "#ef4444";
                      return <Cell key={idx} fill={color} fillOpacity={0.8} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Critical/High alerts */}
          {allStaff.filter(s => s.risk_level === "critical" || s.risk_level === "high").length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={13} className="text-red-400" />
                <span className="text-[10px] text-red-400 font-black uppercase tracking-widest">Fatigue Alerts</span>
              </div>
              <div className="space-y-2">
                {allStaff.filter(s => ["critical", "high"].includes(s.risk_level)).map(s => (
                  <div key={s.id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-sm font-bold text-white">{s.name}</span>
                      <span className="text-[10px] text-slate-500 ml-2">{s.station}</span>
                      <div className="text-[10px] text-red-400 mt-0.5">
                        {s.violations[0] || `Score: ${s.fatigue_score}`}
                      </div>
                    </div>
                    <span className={`text-[9px] font-black uppercase border px-2 py-1 rounded-full ${RISK_STYLE[s.risk_level]?.badge}`}>
                      {s.risk_level}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staff fatigue list */}
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3">
              Staff Assessment ({filtered.length})
            </div>
            <div className="space-y-2">
              {filtered.map(s => {
                const isOpen = expanded === s.id;
                const style  = RISK_STYLE[s.risk_level] || RISK_STYLE.low;
                return (
                  <div
                    key={s.id}
                    className={`rounded-xl border overflow-hidden cursor-pointer ${style.badge}`}
                    onClick={() => setExpanded(isOpen ? null : s.id)}
                    data-testid={`fatigue-staff-${s.id}`}
                  >
                    {/* Main row */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <ScoreGauge score={s.fatigue_score} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-white">{s.name}</span>
                          <span className={`text-[8px] font-black uppercase border px-1.5 py-0.5 rounded ${style.badge}`}>
                            {s.risk_level}
                          </span>
                          {!s.fit_for_duty && (
                            <span className="text-[8px] text-red-400 font-bold border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 rounded">
                              VIOLATION
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 flex gap-3 flex-wrap">
                          <span>{s.station}</span>
                          <span>·</span>
                          <span className="capitalize">{s.role.replace(/_/g, " ")}</span>
                          <span>·</span>
                          <span>{s.hours_24h}h today</span>
                          <span>·</span>
                          <span>{s.hours_week}h this week</span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div className="px-4 pb-4 border-t border-white/5 bg-black/20 pt-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: "24h Duty",        value: `${s.hours_24h}h`, limit: `Max ${thresholds.max_hours_24h}h`, over: s.hours_24h > thresholds.max_hours_24h },
                            { label: "7-Day Hours",     value: `${s.hours_week}h`, limit: `Max ${thresholds.max_hours_week}h`, over: s.hours_week > thresholds.max_hours_week },
                            { label: "Last Rest",       value: `${s.last_rest_hours}h`, limit: `Min ${thresholds.min_rest_hours}h`, over: s.last_rest_hours < thresholds.min_rest_hours },
                            { label: "Consec. Days",    value: `${s.consec_days}d`, limit: `Max ${thresholds.max_consec_days}d`, over: s.consec_days > thresholds.max_consec_days },
                          ].map(item => (
                            <div key={item.label} className={`rounded-lg px-3 py-2 ${item.over ? "bg-red-500/10 border border-red-500/20" : "bg-slate-800/40"}`}>
                              <div className="text-[9px] text-slate-500 uppercase">{item.label}</div>
                              <div className={`text-sm font-black mt-0.5 ${item.over ? "text-red-400" : "text-white"}`}>{item.value}</div>
                              <div className="text-[8px] text-slate-600">{item.limit}</div>
                            </div>
                          ))}
                        </div>
                        {s.violations.length > 0 && (
                          <div className="space-y-1 mt-1">
                            {s.violations.map((v, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-[10px] text-red-400">
                                <AlertTriangle size={9} className="mt-0.5 flex-shrink-0" />
                                {v}
                              </div>
                            ))}
                          </div>
                        )}
                        {s.violations.length === 0 && (
                          <div className="flex items-center gap-1.5 text-[10px] text-green-400">
                            <CheckCircle size={10} /> No regulatory violations — fit for duty
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
